import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import { ArrowBack, Add, Edit, Delete, Class as ClassIcon } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { principalAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import ResponsiveDataView from '@components/mobile/ResponsiveDataView';

const Classes = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');
  const { user, logout } = useAuth();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, classId: null, className: '' });
  const [error, setError] = useState('');
  const [highlightedRowId, setHighlightedRowId] = useState(null);

  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      try {
        const response = await principalAPI.getClasses();
        if (isMounted) {
          setClasses(response.data.classes || []);
          setError('');
        }
      } catch (error) {
        console.error('Error fetching classes:', error);
        if (isMounted) {
          setClasses([]);
          setError('Failed to load classes');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchData();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // fetchClasses duplicate removed; initial fetch handled in useEffect above

  useEffect(() => {
    if (!highlightId || loading || classes.length === 0) return;
    setHighlightedRowId(highlightId);
    const el = document.getElementById(`class-${highlightId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('highlight');
      return next;
    }, { replace: true });
    const timer = setTimeout(() => setHighlightedRowId(null), 2500);
    return () => clearTimeout(timer);
  }, [highlightId, loading, classes, setSearchParams]);

  const handleDeleteClick = (classItem) => {
    setDeleteDialog({ open: true, classId: classItem.id, className: classItem.name });
  };

  const handleDeleteConfirm = async () => {
    try {
      await principalAPI.deleteClass(deleteDialog.classId);
      setClasses(classes.filter(c => c.id !== deleteDialog.classId));
      setDeleteDialog({ open: false, classId: null, className: '' });
      setError('');
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to delete class');
      setDeleteDialog({ open: false, classId: null, className: '' });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, classId: null, className: '' });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Manage Classes
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/principal/dashboard')}
          >
            Back to Dashboard
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/principal/classes/new')}
          >
            Add Class
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {classes.length === 0 ? (
        <Paper>
          <Typography color="text.secondary" align="center" py={3}>
            No classes found. Click "Add Class" to create one.
          </Typography>
        </Paper>
      ) : (
        <ResponsiveDataView
          rows={classes}
          keyExtractor={(cls) => cls.id}
          renderCard={(cls) => (
            <Card
              variant="outlined"
              id={`class-${cls.id}`}
              sx={{
                mb: 1.5,
                ...(highlightedRowId === cls.id && { bgcolor: 'rgba(102,126,234,0.12)', transition: 'background-color 0.5s' }),
              }}
            >
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <ClassIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" fontWeight="bold">{cls.name}</Typography>
                    {cls.section && <Chip label={cls.section} size="small" variant="outlined" />}
                  </Box>
                  <Box>
                    <IconButton size="small" color="primary" title="Edit" onClick={() => navigate(`/principal/classes/${cls.id}/edit`)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" title="Delete" onClick={() => handleDeleteClick(cls)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {cls.teacher_name || 'Not Assigned'} · {cls.student_count || 0} students
                </Typography>
              </CardContent>
            </Card>
          )}
          table={
            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Class Name</strong></TableCell>
                    <TableCell><strong>Section</strong></TableCell>
                    <TableCell><strong>Class Teacher</strong></TableCell>
                    <TableCell><strong>Total Students</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {classes.map((cls) => (
                    <TableRow
                      key={cls.id}
                      id={`class-${cls.id}`}
                      hover
                      sx={highlightedRowId === cls.id ? { bgcolor: 'rgba(102,126,234,0.12)', transition: 'background-color 0.5s' } : undefined}
                    >
                      <TableCell>{cls.name}</TableCell>
                      <TableCell>{cls.section}</TableCell>
                      <TableCell>{cls.teacher_name || 'Not Assigned'}</TableCell>
                      <TableCell>{cls.student_count || 0}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="primary"
                          title="Edit"
                          onClick={() => navigate(`/principal/classes/${cls.id}/edit`)}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          title="Delete"
                          onClick={() => handleDeleteClick(cls)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          }
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={handleDeleteCancel} fullWidth maxWidth="sm">
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteDialog.className}</strong>?
          </Typography>
          <Typography color="error" sx={{ mt: 1 }}>
            ⚠️ This will unassign all students from this class.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Classes;
